import React from "react";

import SectionHeading from "components/SectionHeading";

import { MeanAbsoluteChart, ToleranceBandChart, WithinBandChart } from "./accuracyCharts";
import sharedClasses from "../style.module.scss";
import classes from "../docTables.module.scss";

const TOLERANCE_BANDS = [
    { band: "±0.1%", files: "4,729", share: "81.3%", shareValue: 81.3 },
    { band: "±0.5%", files: "5,234", share: "89.9%", shareValue: 89.9 },
    { band: "±1%", files: "5,493", share: "94.4%", shareValue: 94.4 },
    { band: "±3%", files: "5,798", share: "99.6%", shareValue: 99.6 },
    { band: "±5%", files: "5,817", share: "99.9%", shareValue: 99.9 },
];

const END_USES = [
    {
        name: "Total annual consumption",
        meanAbs: "0.156%",
        meanAbsValue: 0.156,
        within1: "94.4%",
        within1Value: 94.4,
        within5: "99.9%",
        within5Value: 99.9,
    },
    {
        name: "Space heating",
        meanAbs: "0.279%",
        meanAbsValue: 0.279,
        within1: "90.1%",
        within1Value: 90.1,
        within5: "99.6%",
        within5Value: 99.6,
    },
    {
        name: "Domestic hot water",
        meanAbs: "0.028%",
        meanAbsValue: 0.028,
        within1: "100%",
        within1Value: 100,
        within5: "100%",
        within5Value: 100,
    },
    {
        name: "Ventilation (electrical)",
        meanAbs: "0.013%",
        meanAbsValue: 0.013,
        within1: "99.9%",
        within1Value: 99.9,
        within5: "99.9%",
        within5Value: 99.9,
    },
    {
        name: "Auxiliary energy",
        meanAbs: "0.287%",
        meanAbsValue: 0.287,
        within1: "89.8%",
        within1Value: 89.8,
        within5: "99.6%",
        within5Value: 99.6,
    },
    {
        name: "Gross heat loss",
        meanAbs: "0.264%",
        meanAbsValue: 0.264,
        within1: "89.4%",
        within1Value: 89.4,
        within5: "99.9%",
        within5Value: 99.9,
    },
    {
        name: "Electrical baseload",
        meanAbs: "0.000%",
        meanAbsValue: 0,
        within1: "100%",
        within1Value: 100,
        within5: "100%",
        within5Value: 100,
    },
];

export default () => (
    <div>
        <SectionHeading
            title="Standard Operating Conditions Approximation"
            subtitle="Approximating SOC in General-mode simulations for quick comparisons and exploratory work."
        />
        <div className={sharedClasses.contentWrapper}>
            <section className={classes.section}>
                <h3>About</h3>
                <p>
                    Standard operating conditions (SOC) are used to normalize for occupant behaviour in building energy
                    simulations so that decisions can be made based on building science and the building asset itself.
                </p>
                <p>
                    When simulating in General mode in Volta SNAP, Model Settings includes an option to{" "}
                    <strong>Approximate SOC in General mode</strong>. When toggled on, Volta SNAP simulates an altered
                    copy of the house so the run approximates SOC in General mode. Saved model inputs and upgrade
                    packages remain unchanged.
                </p>
                <p>
                    After a simulation with the setting on, results will be labeled{" "}
                    <strong>General mode — SOC approximated</strong>. Simulations must be re-run after the setting is
                    toggled on or off to see the impact.
                </p>
                <p>
                    The control is only available for General-mode files. EnerGuide and Ontario Reference House files
                    already run SOC in their program mode. A code reference house is a separate comparison. See{" "}
                    <a href="/documentation/reference-houses">Reference Houses</a>.
                </p>
                <p>All parametric and community analyses are run in General mode with the approximation on.</p>
            </section>

            <section className={classes.section}>
                <h3>Accuracy</h3>
                <p>
                    Volta SNAP's SOC approximation does not make a file equivalent to a HOT2000 file running in
                    EnerGuide Rating System or Ontario Reference House modes. The approximation is suitable for quick
                    comparisons and exploratory work in General mode, which runs simulations ~5x faster than other
                    modes. Users should not imply that the approximation is equivalent to a native SOC simulation.
                </p>
                <p>
                    The approximation was compared with SOC baseline results from 5,820 archetype files available in
                    NRCan's Housing Archetypes for Energy Analysis:{" "}
                    <a
                        href="https://github.com/canmet-energy/housing-archetypes"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        https://github.com/canmet-energy/housing-archetypes
                    </a>
                </p>
                <p>
                    The median difference in total annual consumption is 0%. The mean signed difference is −0.096%. The
                    mean absolute difference is 0.156% of annual consumption, or 0.24 GJ. 90% of files are within 0.51%
                    (0.74 GJ). 95% are within 1.11% (1.61 GJ). The largest absolute difference in the set is 6.56% (13.9
                    GJ).
                </p>
                <div className={classes.tableWrapper}>
                    <table className={classes.factorTable}>
                        <thead>
                            <tr>
                                <th>Total consumption band</th>
                                <th>Paired files</th>
                                <th>Share of 5,820</th>
                            </tr>
                        </thead>
                        <tbody>
                            {TOLERANCE_BANDS.map((row) => (
                                <tr key={row.band}>
                                    <td>{row.band}</td>
                                    <td>{row.files}</td>
                                    <td>{row.share}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <ToleranceBandChart rows={TOLERANCE_BANDS} />
                {/* <p className={classes.referencesNote}>
                    Shares are rounded to one decimal place. 5,817 of 5,820 files fall inside ±5%.
                </p> */}

                <div className={classes.tableWrapper}>
                    <table className={classes.factorTable}>
                        <thead>
                            <tr>
                                <th>End use</th>
                                <th>Mean absolute difference</th>
                                <th>Within ±1%</th>
                                <th>Within ±5%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {END_USES.map((row) => (
                                <tr key={row.name}>
                                    <td>{row.name}</td>
                                    <td>{row.meanAbs}</td>
                                    <td>{row.within1}</td>
                                    <td>{row.within5}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <MeanAbsoluteChart rows={END_USES} />
                <WithinBandChart rows={END_USES} />
                <p className={classes.referencesNote}>
                    Ventilation percentages use the 4,976 files whose SOC baseline ventilation was not zero. Electrical
                    baseload matches the baseline on every paired file. Domestic hot water is within ±1% on every paired
                    file. The remaining difference sits mainly in space heating, auxiliary energy, and gross heat loss.
                </p>

                <ul className={classes.references}>
                    <li>
                        Suitable for quick comparisons and exploratory work in General mode. Does not produce an
                        official EnerGuide rating.
                    </li>
                </ul>
            </section>
        </div>
    </div>
);
